<script setup>
/**
 * "Restore how it was" (P-6.11): the saves of ONE thing, newest first, each restored in ONE
 * tap. No confirm, on purpose: restoring brings back how it was just before that save, and is
 * itself a save, so the same list takes her straight forward again.
 *
 * IT RENDERS NOTHING UNLESS IT HAS A ROW. A thing never saved twice has no history worth a
 * section, and a history that could not be read is not worth an error box under every editor:
 * the page it sits on keeps working either way, and the next visit asks again. That is also why
 * a page spec that mocks the API without these methods renders exactly as it did before.
 */
import { computed, ref, watch } from 'vue';

import { useAlert } from 'dashboard/composables';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { toIntlLocale } from '../helpers/intl.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  /** `personality`, `task`, `menu`, `connection` or `answer`. */
  kind: { type: String, required: true },
  /** The thing's id; ignored for `personality`, of which there is one. */
  itemId: { type: Number, default: null },
});

const emit = defineEmits(['restored']);

const { t, locale } = useWasloI18n();

const canAsk = computed(
  () =>
    props.accountId !== null &&
    (props.kind === 'personality' || props.itemId !== null)
);

const history = useWasloApi(
  signal =>
    canAsk.value
      ? wasloApi.getRevisions(
          props.accountId,
          props.kind,
          props.kind === 'personality' ? null : props.itemId,
          signal
        )
      : Promise.resolve([]),
  { isEmpty: () => false }
);

watch(
  () => [props.accountId, props.kind, props.itemId],
  () => history.reload()
);

const rows = computed(() =>
  Array.isArray(history.data.value) ? history.data.value : []
);

const restoringId = ref(null);
const failure = ref('');

const whenOf = at => {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(toIntlLocale(locale.value), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
};

const whoOf = by => t(`WASLO.UNDO.WHO.${String(by).toUpperCase()}`);

const restore = async row => {
  if (restoringId.value !== null || props.accountId === null) return;
  restoringId.value = row.id;
  failure.value = '';
  try {
    const result = await wasloApi.restoreRevision(props.accountId, row.id);
    useAlert(
      t(
        result.switchedOff
          ? 'WASLO.UNDO.HISTORY.RESTORED_OFF'
          : 'WASLO.UNDO.HISTORY.RESTORED'
      )
    );
    emit('restored', result);
    await history.reload();
  } catch (error) {
    failure.value = t(
      error?.status === 409
        ? 'WASLO.UNDO.HISTORY.CONFLICT'
        : 'WASLO.UNDO.HISTORY.FAILED'
    );
  } finally {
    restoringId.value = null;
  }
};

defineExpose({ reload: () => history.reload() });
</script>

<template>
  <section
    v-if="rows.length"
    class="flex flex-col gap-4"
    data-waslo-restore-history
  >
    <SettingsHeader
      :heading="t('WASLO.UNDO.HISTORY.HEADING')"
      :description="t('WASLO.UNDO.HISTORY.DESCRIPTION')"
    />
    <Banner v-if="failure" color="ruby" data-waslo-restore-error>
      {{ failure }}
    </Banner>
    <ul
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    >
      <li
        v-for="row in rows"
        :key="row.id"
        class="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
        :data-waslo-restore-row="row.id"
      >
        <p class="mb-0 text-sm text-n-slate-12">
          {{
            t('WASLO.UNDO.HISTORY.ROW', {
              when: whenOf(row.at),
              who: whoOf(row.by),
            })
          }}
        </p>
        <Button
          :label="t('WASLO.UNDO.HISTORY.RESTORE')"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-rotate-ccw"
          :is-loading="restoringId === row.id"
          data-waslo-restore
          @click="restore(row)"
        />
      </li>
    </ul>
  </section>
</template>
